<template>
  <div>
    <h4>{{ t('contentTypeConfiguration.csvMapping') }}</h4>
    <table-wrapper
      :fields="tableFields"
      :filter-display="undefined"
      :loading="!dependenciesFetched"
      :items="mappingFields!"
      :page-options="pageOptions"
      :paginator="false"
      :row-class="rowClassFunction"
    >
      <template #cell(fieldDisplayName)="{ data }">
        <div
          v-if="data.field === 'Filepath'"
          class="flex gap-2 items-center"
        >
          {{ t(`contentTypeFields.${data.fieldDisplayName}`) }}
          <div
            v-tooltip.right="{
              value: t('documentDetailView.filepathInfo'),
              pt: {
                text: 'w-[20rem]',
              },
            }"
          >
            <font-awesome-icon :icon="['fas', 'circle-info']" />
          </div>
        </div>
        <div v-else>
          {{ t(`contentTypeFields.${data.fieldDisplayName}`) }}
        </div>
      </template>
      <template #cell(importValueType)="{ data }">
        <div v-if="data" class="import-field-container">
          <PVSelect
            :id="`${data.field.replace(/ /g, '_')}-type-select`"
            v-model="selectedAssignments[data.field].type"
            class="import-field-type-select"
            fluid
            :options="importValueTypeSelectOptions"
            option-label="text"
            option-value="value"
            @value-change="setInputType(data.field)"
          >
          </PVSelect>
        </div>
      </template>

      <template #cell(importValue)="{ data }">
        <div v-if="data && possibleTaxonomyFieldValues">
          <PVSelect
            v-if="selectedAssignments[data?.field]?.type === 'csvField'"
            :id="`${data?.fieldDisplayName.replace(/ /g, '_')}-select`"
            v-model="selectedAssignments[data?.field].value"
            class="import-field-select"
            fluid
            :options="
              headers!.length
                ? [...headers, '']
                : [selectedAssignments[data?.field].value]
            "
          >
          </PVSelect>

          <template
            v-if="
              selectedAssignments[data?.field]?.type === 'constantAssignment'
            "
          >
            <PVInputText
              v-if="
                !Object.keys(possibleTaxonomyFieldValues!)?.includes(
                  data?.field,
                )
              "
              :id="`${data?.field.replace(/ /g, '_')}-constant-input`"
              v-model="selectedAssignments[data?.field].value"
              class="constant-value-input"
              fluid
            />

            <PVSelect
              v-else
              :id="`${data?.field.replace(/ /g, '_')}-constant-select`"
              v-model="selectedAssignments[data?.field].value"
              fluid
              :options="['', ...possibleTaxonomyFieldValues?.[data?.field]]"
            />
          </template>
        </div>
      </template>
    </table-wrapper>

    <message-wrapper
      v-if="validationError"
      class="error-message"
      content-centred
      severity="error"
    >
      {{ validationError }}
    </message-wrapper>

    <div class="buttons-container">
      <vf-button
        id="cancel-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.tertiary"
        @click="handleCancelButtonClick"
      >
        {{ t('generic.cancel') }}
      </vf-button>

      <vf-button
        v-if="dependenciesFetched"
        :size="buttonSizes.large"
        :variant="buttonVariants.primary"
        @click="submitMapping"
      >
        {{ t('generic.save') }}
      </vf-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { useToast } from '@/composables/useToast';
  import axios from 'axios';
  import { cloneDeep, set } from 'lodash';
  import endpointsList from '../../helpers/endpointsList';
  import universalMappingFields from './universalMappingFields';
  import {
    AssignmentType,
    ContentTypeFieldType,
    ICheckBotMapping,
    IContentTypeField,
    ICreateDigitalizationProjectContentTypeMappingDto,
    IDigitalizationProjectContentTypeFieldMappingDto,
    IFieldMapping,
    IGetContentTypeAndCheckBotMappingDto,
    ProjectType,
    Serialized,
    TermsPerTermSet,
    UniversalMappingField,
  } from 'types';
  import { reverseKeyValuePairs } from 'utils';
  import {
    TableFieldsBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { Route, routeParams } from '@/routes';
  import { ProjectsSupplierDetailViewTab } from '@/views/ProjectsSupplierDetailView/ProjectsSupplierDetailView.types';
  import { DigitalizationProjectDetailsTab } from '@/views/DigitalizationProjectDetails/DigitalizationProjectDetails.types';

  interface ISelectedAssignments {
    [field: string]: ISelectedAssignment;
  }
  interface ISelectedAssignment {
    type: AssignmentType;
    value: string;
  }

  interface IReversedCheckBotMapping {
    [field: string]: keyof ICheckBotMapping;
  }

  type MappingField = IFieldMapping | IContentTypeField;

  const { t } = useI18n();
  const router = useRouter();
  const { createSuccessToast, createErrorToast } = useToast();

  const props = defineProps<{
    contentTypeId: string;
    headers: string[];
    mode: ProjectType;
    projectId: string;
    supplierId: string;
  }>();

  const checkBotMapping = ref<IReversedCheckBotMapping>({});
  const fieldsWithValidationError = ref<string[]>([]);
  const mappingFields = ref<IFieldMapping[] | IContentTypeField[] | undefined>(
    undefined,
  );
  const possibleTaxonomyFieldValues = ref<TermsPerTermSet | undefined>(
    undefined,
  );
  const selectedAssignments = ref<ISelectedAssignments>({});
  const validationError = ref<string | false>(false);

  const { pageOptions } = useTableWrapper<MappingField>(
    TableMode.CLIENT_SIDE,
    'fieldDisplayName',
    getMapping,
  );

  async function getMapping() {
    if (props.mode === ProjectType.SUPPLIER_PROJECT) {
      await getContentTypeMapping();
      await getCheckBotMapping();
    } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      getDigitalizationContentTypeAndCheckBotMapping();
    }
    await getPossibleTaxonomyFieldValues();
  }

  async function getContentTypeMapping() {
    try {
      const response = await axios.get<IFieldMapping[]>(
        endpointsList.contentTypeMappings.getFieldsMappingConfiguration(
          props.contentTypeId!,
          props.projectId!,
          props.supplierId!,
        ),
      );

      if (!response.data.length) {
        await getFields();
        return;
      }

      mappingFields.value = response.data;
      initializeContentTypeMappingForm();
    } catch {
      createErrorToast(
        t('contentTypeConfiguration.errors.errorGettingMapping'),
      );
    }
  }

  function initializeContentTypeMappingForm() {
    if (!mappingFields.value) return;

    for (const field of mappingFields.value as IFieldMapping[]) {
      set(selectedAssignments.value, `${field.field}.value`, field.value ?? '');
      set(
        selectedAssignments.value,
        `${field.field}.type`,
        field.assignmentType ?? AssignmentType.csvField,
      );
    }
  }

  async function getCheckBotMapping() {
    try {
      const response = await axios.get<ICheckBotMapping>(
        endpointsList.contentTypeMappings.getCheckBotMapping(
          props.contentTypeId!,
        ),
      );

      checkBotMapping.value = reverseKeyValuePairs(
        response.data as unknown as Record<string, string>,
      ) as IReversedCheckBotMapping;
    } catch {
      //
    }
  }

  async function getFields() {
    try {
      const response = await axios.get<IContentTypeField[]>(
        endpointsList.contentTypeMappings.getContentTypeById(
          props.contentTypeId!,
        ),
      );
      const fields = response.data;

      if (!fields.some((field) => field.field.toLowerCase() === 'kks')) {
        fields.unshift({
          field: 'KKS',
          fieldDisplayName: 'KKS',
          dataType: ContentTypeFieldType.TEXT,
          required: true,
        });
      }

      if (
        fields.find(
          (field) => field.field.toLowerCase() === 'kks' && !field.required,
        )
      ) {
        const kksField = fields.find(
          (field) => field.field.toLowerCase() === 'kks',
        );
        kksField!.required = true;
      }

      for (const field of universalMappingFields) {
        fields.unshift(field);
      }

      mappingFields.value = fields;

      for (const field of fields) {
        set(selectedAssignments.value, `${field.field}.value`, '');
        set(selectedAssignments.value, `${field.field}.type`, 'csvField');
      }
    } catch {
      createErrorToast(t('errors.error'));
    }
  }

  function validateMapping() {
    let isValidated = true;

    fieldsWithValidationError.value = [];
    validationError.value = false;

    const requiredFields: string[] = [];

    for (const field of mappingFields.value ?? []) {
      if (field.required) {
        requiredFields.push(field.field);
      }
    }

    for (const field of requiredFields) {
      if (
        selectedAssignments.value[field].value === '' &&
        selectedAssignments.value[field].type !== 'currentDate'
      ) {
        fieldsWithValidationError.value.push(field);
        isValidated = false;

        validationError.value = t(
          'contentTypeConfiguration.selectForAllFields',
        );
      }
    }

    return isValidated;
  }

  function rowClassFunction(item: IFieldMapping | IContentTypeField) {
    return fieldsWithValidationError.value.includes(item.field)
      ? 'table-danger'
      : '';
  }

  async function submitFieldsMapping() {
    const mappedFields: IFieldMapping[] = [];

    for (const field of mappingFields.value ?? []) {
      const mappedField = {
        field: field.field,
        fieldDisplayName: field.fieldDisplayName,
        dataType: field.dataType,
        value: selectedAssignments.value[field.field].value,
        assignmentType: selectedAssignments.value[field.field].type,
        required: field.required,
      };

      mappedFields.push(mappedField);
    }

    const patchObject = {
      fieldsMapping: mappedFields,
    };

    try {
      const response = await axios.patch(
        endpointsList.contentTypeMappings.addFieldsMapping(
          props.contentTypeId!,
          props.projectId!,
          props.supplierId!,
        ),
        patchObject,
      );

      if (response.status === 200) {
        createSuccessToast(t('contentTypeConfiguration.mappingSaved'));
      }

      goToProjectsSupplierDetailView();
    } catch {
      createErrorToast(t('errors.error'));
    }
  }

  function handleCancelButtonClick() {
    if (props.mode === ProjectType.SUPPLIER_PROJECT) {
      goToProjectsSupplierDetailView();
    } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      goToDigitalizationProjectSettings();
    }
  }

  function goToProjectsSupplierDetailView() {
    router.push({
      name: Route.PROJECT_SUPPLIER_DETAILS,
      params: {
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].projectId]:
          props.projectId,
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].supplierId]:
          props.supplierId,
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].tab]:
          ProjectsSupplierDetailViewTab.CONTENT_TYPES,
      },
    });
  }

  function goToDigitalizationProjectSettings() {
    router.push({
      name: Route.DIGITALIZATION_PROJECT_DETAILS,
      params: {
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]:
          props.projectId,
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].tab]:
          DigitalizationProjectDetailsTab.SETTINGS,
      },
    });
  }

  function submitMapping() {
    if (!validateMapping()) {
      return;
    }

    if (props.mode === ProjectType.SUPPLIER_PROJECT) {
      submitFieldsMapping();
    } else if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      submitDigitalizationContentType();
    }
  }

  function setInputType(field: string) {
    selectedAssignments.value[field].value = '';
    selectedAssignments.value = cloneDeep(selectedAssignments.value);
  }

  function contentTypeField(mapping: IFieldMapping): string {
    let fieldName = mapping.fieldDisplayName;

    if (fieldName === UniversalMappingField.DOCUMENT_ID) {
      fieldName = 'DocumentID';
    }

    if (mapping.required) {
      fieldName = `${fieldName}*`;
    }
    if (checkBotMapping.value[mapping.field]) {
      fieldName = `${fieldName} (${checkBotMapping.value[mapping.field]})`;
    }

    return fieldName;
  }

  async function getPossibleTaxonomyFieldValues() {
    try {
      const response = await axios.get(
        endpointsList.contentTypeMappings.getPossibleValuesForContentTypesTaxonomyFields(
          props.contentTypeId!,
        ),
      );

      possibleTaxonomyFieldValues.value = response.data;
    } catch {
      createErrorToast(
        t('contentTypeConfiguration.errors.couldNotGetPossibleTaxonomyValues'),
      );
    }
  }

  async function getDigitalizationContentTypeAndCheckBotMapping() {
    try {
      const response = await axios.get<IGetContentTypeAndCheckBotMappingDto>(
        endpointsList.contentTypeMappings.getDigitalizationProjectContentTypeConfig(
          props.contentTypeId!,
          props.projectId!,
        ),
      );

      mappingFields.value = response.data.fieldMappings;
      initializeContentTypeMappingForm();
      checkBotMapping.value = reverseKeyValuePairs(
        response.data.checkBotMapping,
      );
    } catch {
      createErrorToast(
        t('contentTypeConfiguration.errors.errorGettingMapping'),
      );
    }
  }

  async function submitDigitalizationContentType() {
    const fieldMappings: IDigitalizationProjectContentTypeFieldMappingDto[] =
      Object.entries(selectedAssignments.value).map(([field, assignment]) => {
        const checkBotMappingValue = Object.entries(checkBotMapping.value).find(
          ([contentTypeField]) => contentTypeField === field,
        )?.[1];

        return {
          field,
          value: assignment.value,
          assignmentType: assignment.type,
          checkBotMapping: checkBotMappingValue,
        };
      });

    const body: Serialized<ICreateDigitalizationProjectContentTypeMappingDto> =
      {
        contentTypeId: props.contentTypeId!,
        digitalizationProjectId: props.projectId!,
        fieldMappings,
      };

    try {
      await axios.post(
        endpointsList.contentTypeMappings
          .createDigitalizationProjectContentTypeMapping,
        body,
      );

      createSuccessToast(t('contentTypeConfiguration.mappingSaved'));
      goToDigitalizationProjectSettings();
    } catch {
      createErrorToast(t('contentTypeConfiguration.errors.errorSavingMapping'));
    }
  }

  const dependenciesFetched = computed(() => {
    return !!(mappingFields.value && possibleTaxonomyFieldValues.value);
  });

  const tableFields = computed(() => {
    return new TableFieldsBuilder<any>({
      sortable: false,
      labelTranslationKey: 'contentTypeConfiguration',
    })
      .addField('fieldDisplayName', {
        tdClass: ['align-middle'],
        width: '20%',
        label: t('contentTypeConfiguration.contentTypeField'),
        valueGetter: contentTypeField,
      })
      .addField('dataType', { tdClass: ['align-middle'], width: '20%' })
      .addField('importValueType', {
        tdClass: ['align-middle'],
        width: '30%',
        sortable: false,
        label: t('contentTypeConfiguration.importFieldType'),
      })
      .addField('importValue', {
        tdClass: ['align-middle'],
        width: '30%',
        sortable: false,
        label: t('contentTypeConfiguration.importField'),
      })
      .build();
  });

  const importValueTypeSelectOptions = computed(() => {
    return [
      {
        value: 'csvField',
        text: t('contentTypeConfiguration.csvField'),
      },
      {
        value: 'constantAssignment',
        text: t('contentTypeConfiguration.constantValue'),
      },
      {
        value: 'currentDate',
        text: t('contentTypeConfiguration.currentDate'),
      },
    ];
  });
</script>

<style lang="scss" scoped>
  h4 {
    margin-bottom: 40px;
  }

  .error-message {
    margin-top: 2.5rem;
  }

  .buttons-container {
    margin: 2rem 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
</style>
